import {Component, ConfigOptions} from 'panel';

import {reporter} from 'common/report/error-reporting';
import {getCookie} from 'common/util/cookie';
import {executeRecaptchaPromise} from 'common/util/recaptcha';
import {isValidEmailAddress} from 'common/util/string';

import {track, TrackEvents, TrackProperties} from '../tracking';
import template from './index.jade';

interface ForgotPasswordState {
  email: string;
  validationError: string | null;
}

export class ForgotPassword extends Component<ForgotPasswordState> {
  private form: HTMLFormElement;

  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'recaptcha-key': {type: `string`, description: `key needed for recaptcha to work`},
      'success-message': {type: `string`, description: `possible notice shown in banner`},
      'error-message': {type: `string`, description: `error from API, shown in banner`},
      next: {type: `string`, description: `next url`},
    };
  }

  get config(): ConfigOptions<ForgotPasswordState> {
    return {
      template,
      defaultState: {
        email: ``,
        validationError: null,
      },
      helpers: {
        onFormInserted: ({elm}) => {
          this.form = elm;
        },

        handleEmailInput: (ev) => {
          const email = ev.target.value;
          this.update({email});
        },

        handleEmailBlur: () => {
          this.validateEmail();
        },

        handleSubmit: (ev) => {
          ev.preventDefault();
          this.helpers.handleSubmitClick(); // async but don't await, needs to be synchronous for ev.preventDefault
        },

        handleSubmitClick: async () => {
          (await this.validate()) && this.form.submit();
        },

        getCsrfToken: () => {
          return getCookie(`csrftoken`);
        },
      },
    };
  }

  connectedCallback(): void {
    super.connectedCallback();

    track(TrackEvents.ViewForgotPasswordPage, {
      [TrackProperties.PageVersion]: `Forgot Password Page`,
    });
  }

  private async validateRecaptcha() {
    let validationError = ``;
    try {
      await executeRecaptchaPromise();
    } catch (err) {
      reporter.error(err);
      validationError = `Recaptcha failed. Please refresh this page and retry.`;
      this.update({validationError});
    }
    return !validationError;
  }

  private validateEmail() {
    const validationError = isValidEmailAddress(this.state.email) ? null : `Please enter a valid email.`;
    this.update({validationError});
    return !validationError;
  }

  private validate() {
    return this.validateEmail() && this.validateRecaptcha();
  }
}

customElements.define(`mp-forgot-password`, ForgotPassword);
